/** LMNT's engine pages (docs/engine-page-contract.md §2): every page that shows
 *  core's Replenishment runs, the module it reads them for, and the URL controls
 *  Nucleus may set on it. The shell merges each onto the route registry entry at
 *  its path (`product.enginePages`, main.tsx), so core can open a page on a run
 *  (`?run_id=<n>`) and set its controls through URLs it builds and validates.
 *
 *  Built, not written out: one entry per visible tab of every Replenishment
 *  mount (pages.ts, which the router mounts too), each tab's params from the
 *  same defaults and option lists its own view reads. A tab, a choice or a
 *  default changes here by changing on the page. Hidden tabs and report tabs
 *  are not declared — off the bar is off Nucleus's menu too. Segments and
 *  filters (JSON) are left to the page.
 */
import { defineEnginePage, type EnginePageDeclaration, type EngineParamSpec } from "@atomic/app-shell";
import {
  BUILD_STAGE_OPTIONS,
  BUILD_UNIT_OPTIONS,
  buildPeriodOptionsFor,
  buildMeasureOrderFor,
  buildViewParamsFor,
} from "./containers/ReplenishmentView/buildPlanConstants";
import {
  ALL_STAGES,
  FINISHED_GOOD_STAGE,
  MODULE_CODE,
  SEGMENT_OPTIONS,
  STAGE_OPTIONS,
  UNALLOCATED_LABEL,
  UNALLOCATED_MODES,
  WALK_UNIT_OPTIONS,
  WALK_PERIOD_OPTIONS,
  WALK_SPEC,
  WALK_VIEW_PARAMS,
  stagedWalkViewParamsFor,
  type Stage,
} from "./containers/ReplenishmentView/constants";
import { REPLENISHMENT_PAGES, type ReplenishmentPage } from "./containers/ReplenishmentView/pages";
import { pageTabs, type ReplenishmentTab } from "./containers/ReplenishmentView/tabs";
import { NAV_EXTRAS } from "./rail";
import { ROUTE_REGISTRY_EXTRAS } from "./routeRegistry";

/** The kit's own control labels (ViewControls' defaults), as the bar shows them. */
const AGGREGATE_BY = "Aggregate by";
const DISPLAY_BY = "Display by";
const DATE_RANGE = "Date Range";

type Options = readonly { value: string; disabled?: boolean }[];
/** What a control offers: its options, less any shown but not choosable. */
const offered = (options: Options): string[] => options.filter((o) => !o.disabled).map((o) => o.value);

const choice = (values: readonly string[], label: string, fallback?: string): EngineParamSpec => ({
  type: "enum",
  values: [...new Set(values)],
  ...(fallback !== undefined ? { default: fallback } : {}),
  label,
});

/** The Balance Walk's controls (ReplenishmentCube). A walk with stages (the
 *  staged page, a class page) carries the stage; its defaults are the opening
 *  stage's, and a default that differs by stage is left undeclared. */
function walkParams(page: ReplenishmentPage, tab: ReplenishmentTab): Record<string, EngineParamSpec> {
  const staged = Boolean(page.walkStages) || page.mode === "plan";
  const tabStages = tab.stages ?? ALL_STAGES;
  const stages: readonly Stage[] = staged
    ? STAGE_OPTIONS.filter((o) => tabStages.includes(o.value)).map((o) => o.value)
    : [FINISHED_GOOD_STAGE];
  const defaultsAt = (stage: Stage | null): typeof WALK_VIEW_PARAMS & { stage?: string } =>
    staged ? stagedWalkViewParamsFor(stage, tabStages, page.lockedClass) : WALK_VIEW_PARAMS;
  const defaults = defaultsAt(null);
  const sortDefaults = new Set(stages.map((s) => defaultsAt(s).sortBy));
  // The walk's own view controls (engine-page contract §2, a walk page's
  // obligation): the grouping and the read's predicates, in the URL's grammar
  // (useViewParams: JSON; a filter is the bar's [column, operator, values]).
  const dimensions = SEGMENT_OPTIONS.map((o) => o.value);
  return {
    ...(stages.length > 1 ? { stage: choice(stages, "Stage", defaults.stage) } : {}),
    segments: { type: "segments", values: dimensions, label: "Segment by" },
    filters: { type: "filters", columns: dimensions, grammar: "tuples", label: "Filter by" },
    sortBy: choice(
      stages.flatMap((s) => WALK_SPEC[s].measureOrder),
      AGGREGATE_BY,
      sortDefaults.size === 1 ? defaults.sortBy : undefined,
    ),
    // A walk read in one fixed unit shows it in place of the choice.
    ...(stages.some((s) => !WALK_SPEC[s].units) ? { units: choice(offered(WALK_UNIT_OPTIONS), "Units", defaults.units) } : {}),
    period: choice(offered(WALK_PERIOD_OPTIONS), DISPLAY_BY, defaults.period),
    unallocated: choice(UNALLOCATED_MODES, UNALLOCATED_LABEL, defaults.unallocated),
    // Not dates: a Display by period's first day, as the walk keys it.
    fromKey: { type: "string", label: `${DATE_RANGE} start (MM/DD/YY, the first day of a Display by period)` },
    toKey: { type: "string", label: `${DATE_RANGE} end (MM/DD/YY, the first day of a Display by period)` },
  };
}

/** The Production pivot's controls (BuildPlanCube); the Build Plan tab's view
 *  adds the stage and sets its own grains. */
function productionParams(tab: ReplenishmentTab): Record<string, EngineParamSpec> {
  const defaults = buildViewParamsFor(tab.view);
  return {
    ...(tab.view ? { buildStage: choice(offered(BUILD_STAGE_OPTIONS), "Stage", defaults.buildStage) } : {}),
    buildMeasure: choice(buildMeasureOrderFor(tab.view), AGGREGATE_BY, defaults.buildMeasure),
    buildUnits: choice(offered(BUILD_UNIT_OPTIONS), "Units", defaults.buildUnits),
    buildPeriod: choice(offered(buildPeriodOptionsFor(tab.view)), DISPLAY_BY, defaults.buildPeriod),
  };
}

/** A tab's controls; the Transfers matrix (AllocationCube) keeps none in its URL. */
function tabParams(page: ReplenishmentPage, tab: ReplenishmentTab): Record<string, EngineParamSpec> | undefined {
  if (tab.kind === "walk") return walkParams(page, tab);
  if (tab.kind === "production") return productionParams(tab);
  return undefined;
}

/** What the rail or the registry extras call a page, for a tab's own entry. */
const pageName = (path: string): string =>
  NAV_EXTRAS.find((n) => n.href === path)?.name ?? ROUTE_REGISTRY_EXTRAS.find((e) => e.path === path)?.label ?? path;

export const ENGINE_PAGES: EnginePageDeclaration[] = REPLENISHMENT_PAGES.flatMap((page) =>
  pageTabs(page.basePath, page.mode ?? "all", page.lockedClass)
    .filter((tab) => !tab.hidden && !tab.reportSlug)
    .map((tab) => {
      const params = tabParams(page, tab);
      // The base path is named by its tile or extra; a tab below it, by the page and the tab.
      const label = tab.path === page.basePath ? tab.label : `${pageName(page.basePath)}: ${tab.label}`;
      return defineEnginePage({
        path: tab.path,
        module: MODULE_CODE,
        ...(tab.kind === "walk" ? { kind: "walk" as const } : {}),
        label,
        description: `The ${tab.label} tab of ${pageName(page.basePath)}.`,
        ...(params ? { params } : {}),
      });
    }),
);
